import Link from 'next/link'

import { siteConfig } from '@/config/site'
import { cn } from '@/lib/utils'

export interface BreadcrumbItem {
  name: string
  /** Site-root-relative path, e.g. `/products/lms`. */
  href: string
}

/**
 * One trail, rendered twice: as the visible list and as the BreadcrumbList
 * JSON-LD, both from the same `items` array — so a crumb can never reach the
 * markup Google reads without also being on the page.
 *
 * `items` is explicit rather than derived from usePathname because most of the
 * nested routes here end in a slug — `dypes-kolhapur`, `fee-finance`,
 * `navi-mumbai` — whose readable name lives in a data file, not the URL.
 *
 * No 'use client' directive: the component holds no state, so the same file
 * serves the Server Components and the hero components that are clients.
 */
export function Breadcrumbs({
  items,
  className,
}: {
  items: BreadcrumbItem[]
  className?: string
}) {
  if (items.length === 0) return null

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      // siteConfig.url carries no trailing slash, so the root crumb has to
      // skip the join or it becomes `https://euclideum.com//`.
      item:
        item.href === '/' ? siteConfig.url : `${siteConfig.url}${item.href}`,
    })),
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <nav
        aria-label="Breadcrumb"
        className={cn('text-[13px] text-muted-foreground', className)}
      >
        <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {items.map((item, index) => {
            const isCurrent = index === items.length - 1

            return (
              <li key={item.href} className="flex items-center gap-x-2">
                {isCurrent ? (
                  <span aria-current="page" className="text-foreground">
                    {item.name}
                  </span>
                ) : (
                  <>
                    <Link
                      href={item.href}
                      className={cn(
                        'rounded-sm text-inherit transition-colors hover:text-foreground',
                        'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
                        'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active'
                      )}
                    >
                      {item.name}
                    </Link>

                    <span aria-hidden className="opacity-50">
                      /
                    </span>
                  </>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    </>
  )
}
